<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

import Button from 'dashboard/components-next/button/Button.vue';
import { shortDateTime } from 'dashboard/components-next/Contacts/contactTime';
import {
  isTrackingAttribute,
  trackingLabel,
} from 'dashboard/components-next/Contacts/contactTracking';

// "Atribución y seguimiento": read-only view of the tracking attributes
// (utm_*, gclid, fbclid…) flagged as `tracking_attribute` in the account.
// Values are shown in monospace like the mockup and can be copied as a
// query string.
const props = defineProps({
  contact: { type: Object, required: true },
});

const { t, locale } = useI18n();
const contactAttributes = useMapGetter('attributes/getContactAttributes');

const trackingAttributes = computed(() =>
  (contactAttributes.value || [])
    .filter(isTrackingAttribute)
    .map(attribute => ({
      ...attribute,
      attributeDisplayName: trackingLabel(
        attribute.attributeKey,
        attribute.attributeDisplayName
      ),
      value: props.contact.customAttributes?.[attribute.attributeKey] ?? '',
    }))
);

const hasTrackingAttributes = computed(
  () => trackingAttributes.value.length > 0
);

const valueOf = key =>
  trackingAttributes.value.find(attribute => attribute.attributeKey === key)
    ?.value;

// "google / ppc · prospeccion | marketing" — only when there is a source.
const sourceLine = computed(() => {
  const source = valueOf('utm_source');
  if (!source) return '';
  const medium = valueOf('utm_medium');
  const campaign = valueOf('utm_campaign');
  const head = [source, medium].filter(Boolean).join(' / ');
  return campaign ? `${head} · ${campaign}` : head;
});

const firstContact = computed(() =>
  props.contact.createdAt
    ? t('CONTACTS_LAYOUT.DETAILS.ATTRIBUTION.FIRST_CONTACT', {
        date: shortDateTime(props.contact.createdAt, locale.value),
      })
    : ''
);

const hasAnyValue = computed(() =>
  trackingAttributes.value.some(attribute => attribute.value)
);

const copyParameters = async () => {
  const query = trackingAttributes.value
    .filter(attribute => attribute.value)
    .map(
      attribute =>
        `${attribute.attributeKey}=${encodeURIComponent(attribute.value)}`
    )
    .join('&');
  try {
    await copyTextToClipboard(query);
    useAlert(t('CONTACTS_LAYOUT.DETAILS.ATTRIBUTION.COPIED'));
  } catch {
    useAlert(t('CONTACTS_LAYOUT.DETAILS.ATTRIBUTION.COPY_ERROR'));
  }
};
</script>

<template>
  <section
    v-if="hasTrackingAttributes"
    class="flex flex-col gap-3 px-5 py-[18px] rounded-xl border border-n-weak bg-n-solid-2"
  >
    <div class="flex items-center gap-2.5">
      <h3 class="m-0 text-[13.5px] font-semibold tracking-tight text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.DETAILS.ATTRIBUTION.TITLE') }}
      </h3>
      <span v-if="firstContact" class="ml-auto text-xs text-n-slate-10">
        {{ firstContact }}
      </span>
    </div>

    <div
      v-if="sourceLine"
      class="flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-n-blue-border bg-n-blue-2"
    >
      <span class="size-2 rounded-full bg-n-brand flex-shrink-0" />
      <span class="text-[13px] font-semibold truncate text-n-blue-text">
        {{ sourceLine }}
      </span>
    </div>

    <p v-if="!hasAnyValue" class="m-0 text-xs text-n-slate-10">
      {{ t('CONTACTS_LAYOUT.DETAILS.ATTRIBUTION.EMPTY') }}
    </p>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-0.5">
      <div
        v-for="attribute in trackingAttributes"
        :key="attribute.id"
        class="flex items-center gap-2.5 py-1.5 border-b border-n-weak min-w-0"
      >
        <span class="flex-shrink-0 w-[120px] text-xs truncate text-n-slate-10">
          {{ attribute.attributeDisplayName }}
        </span>
        <span
          v-if="attribute.value"
          class="text-xs font-medium font-mono truncate text-n-slate-12"
          :title="attribute.value"
        >
          {{ attribute.value }}
        </span>
        <span v-else class="text-xs text-n-slate-9">—</span>
      </div>
    </div>

    <div v-if="hasAnyValue" class="flex items-center gap-2">
      <Button
        :label="t('CONTACTS_LAYOUT.DETAILS.ATTRIBUTION.COPY')"
        icon="i-lucide-copy"
        color="slate"
        variant="outline"
        size="xs"
        @click="copyParameters"
      />
    </div>
  </section>
</template>
